"use client";

import React, { useState, useEffect } from "react";
import { Tree } from "@blueprintjs/core";
import Agente from "../../../models/Agente";

function groupBy(list, keyGetter) {
  const map = new Map();
  list.forEach((item) => {
    const key = keyGetter(item);
    const collection = map.get(key);
    if (!collection) {
      map.set(key, [item]);
    } else {
      collection.push(item);
    }
  });
  return map;
}

export default function DocsPicker(props) {
  const [currentReply, setCurrentReply] = useState({ ReplyLink: "" });
  const [nodes, setNodes] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadErrors, setUploadErrors] = useState(null);

  // Detectar si es dispositivo móvil
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  // Limpiar preview cuando se cierra el componente
  useEffect(() => {
    if (!props.isOpen) {
      setCurrentReply({ ReplyLink: "" });
      setSelectedFile(null);
      setFilePreview(null);
      setLoading(false);
      setUploadErrors(null);
    }
    if (props.uploadErrors) {
      setUploadErrors(props.uploadErrors);
      setLoading(false);
    }
  }, [props.isOpen, props.uploadErrors]);

  // Componente de lista para móviles
  const DocsList = ({ items, onSelect }) => {
    return (
      <div className="mobile-image-list">
        {items.map((category) => (
          <div key={category.id} className="image-category">
            <h4 className="category-title">{category.label}</h4>
            <div className="image-grid">
              {category.childNodes.map((item) => (
                <div
                  key={item.id}
                  className={`image-item ${currentReply.Id === item.nodeData.Id ? "selected" : ""}`}
                  onClick={() => {
                    onSelect(item.nodeData);
                    // Limpiar archivo cargado cuando se selecciona un predefinido
                    setSelectedFile(null);
                    setFilePreview(null);
                  }}
                >
                  <div className="image-name">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Obtener documentos de quickReplies
  const docReplies = (props.quickReplies || []).filter((x) => x.ReplyType !== "image" && x.ReplyType !== "text");
  const grouped = Array.from(groupBy(docReplies, (x) => x.CategoryName.String));
  const docNodes = grouped
    .map(([group, items]) => {
      return {
        id: group,
        hasCaret: true,
        label: group === "" ? "(Default)" : group,
        childNodes: items
          .map((i) => {
            return {
              id: i.Id,
              icon: "document",
              label: i.FileName.String,
              nodeData: i,
            };
          })
          .sort((a, b) => (a.label > b.label ? 1 : -1)),
      };
    })
    .sort((a, b) => (a.label > b.label ? 1 : -1));

  useEffect(() => {
    setNodes(docNodes);
    if (isMobile && docNodes.length > 0) {
      const expandedNodes = docNodes.map((node) => {
        return {
          ...node,
          isExpanded: true,
        };
      });
      setNodes(expandedNodes);
    }
    // eslint-disable-next-line
  }, [grouped.length, isMobile]);

  const onNodeCollapse = (node, [nodePath]) => {
    const newState = nodes.map((k, index) => {
      if (nodePath === index) {
        k.isExpanded = false;
        return k;
      }
      return k;
    });
    setNodes(newState);
  };

  const onNodeExpand = (node, [nodePath]) => {
    const newState = nodes.map((k, index) => {
      if (nodePath === index) {
        k.isExpanded = true;
        return k;
      }
      return k;
    });
    setNodes(newState);
  };

  const onNodeClick = (node, nodePath) => {
    if (node.nodeData) {
      setCurrentReply(node.nodeData);
      // Limpiar archivo cargado cuando se selecciona un predefinido
      setSelectedFile(null);
      setFilePreview(null);
      
      const newState = nodes.map((currentNode) => {
        if (!currentNode.hasOwnProperty("childNodes")) {
          return currentNode;
        }
        const newChildren = currentNode.childNodes.map((child) => {
          child.isSelected = false;
          if (child.nodeData && child.nodeData.Id === node.nodeData.Id) {
            child.isSelected = true;
          }
          return child;
        });
        currentNode.childNodes = newChildren;
        return currentNode;
      });
      setNodes(newState);
    } else if (nodePath && nodePath.length > 0) {
      const parentPath = nodePath[0];
      const newState = nodes.map((k, index) => {
        if (parentPath === index) {
          k.isExpanded = !k.isExpanded;
        }
        return k;
      });
      setNodes(newState);
    }
  };

  // Manejar selección de archivo
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setSelectedFile(file);
    // Limpiar selección de documento predefinido cuando se carga un archivo
    setCurrentReply({ ReplyLink: "" });
    
    // Limpiar selección visual de los nodos
    const newState = nodes.map((currentNode) => {
      if (!currentNode.hasOwnProperty("childNodes")) {
        return currentNode;
      }
      const newChildren = currentNode.childNodes.map((child) => {
        child.isSelected = false;
        return child;
      });
      currentNode.childNodes = newChildren;
      return currentNode;
    });
    setNodes(newState);

    // Generar preview según el tipo de archivo
    const fileType = file.type;
    const fileName = file.name.toLowerCase();

    if (fileType === "application/pdf" || fileName.endsWith(".pdf")) {
      // Para PDF, crear una URL para preview
      const fileUrl = URL.createObjectURL(file);
      setFilePreview({
        type: "pdf",
        url: fileUrl,
        name: file.name,
        size: formatFileSize(file.size),
      });
    } else if (
      fileType ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
      fileType === "application/msword" ||
      fileName.endsWith(".doc") ||
      fileName.endsWith(".docx")
    ) {
      setFilePreview({
        type: "word",
        name: file.name,
        size: formatFileSize(file.size),
        icon: "📄",
      });
    } else if (
      fileType ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      fileType === "application/vnd.ms-excel" ||
      fileName.endsWith(".xls") ||
      fileName.endsWith(".xlsx")
    ) {
      setFilePreview({
        type: "excel",
        name: file.name,
        size: formatFileSize(file.size),
        icon: "📊",
      });
    } else if (fileType === "text/plain" || fileName.endsWith(".txt")) {
      // Para archivos de texto, leer el contenido
      const reader = new FileReader();
      reader.onload = (e) => {
        setFilePreview({
          type: "text",
          name: file.name,
          size: formatFileSize(file.size),
          content: e.target.result,
          icon: "📝",
        });
      };
      reader.readAsText(file);
    } else if (fileType.startsWith("image/")) {
      // Para imágenes, crear una URL para preview
      const fileUrl = URL.createObjectURL(file);
      setFilePreview({
        type: "image",
        url: fileUrl,
        name: file.name,
        size: formatFileSize(file.size),
      });
    }
  };

  // Formatear tamaño del archivo
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  useEffect(() => {
    if (props.uploadErrors) {
      setLoading(false);
    }
  }, [props.uploadErrors]);

  // Manejar envío del archivo
  const handleSendFile = async () => {
    setLoading(true);
    if (!selectedFile) return;
    console.log("Archivo a enviar:", selectedFile);
    props.onDocumentClick(selectedFile);
  };

  // Determinar si se muestra carga de documentos
  const canUploadUnreviewed = props.currentAgent && props.currentAgent.can(Agente.RULE_CAN_SEND_DOCS_UNREVIEWED);

  // Si no está abierto, no renderizar nada
  if (!props.isOpen) return null;

  return (
    <div
      className="d-flex flex-column w-100 h-100 left-panel mobile-image-picker"
      style={{ left: "0", zIndex: 1000 }}
    >
      {/* Header */}
      <div
        className="row d-flex flex-row align-items-center p-2 m-0"
        style={{ backgroundColor: "white", minHeight: "65px" }}
      >
        <i
          className="fas fa-arrow-left p-2 mx-3 my-1"
          style={{ fontSize: "1.5rem", cursor: "pointer" }}
          onClick={props.close}
          title="Volver sin enviar documento"
        ></i>
        <div className="font-weight-bold">Enviar documento</div>
      </div>

      {/* Área de contenido */}
      <div
        className="container"
        style={{
          height: isMobile ? "calc(100vh - 130px)" : "90vh",
          overflowY: "auto",
          padding: "20px",
        }}
      >
        {/* Listado de documentos de quickReplies */}
        {docNodes.length > 0 && (
          <div className="mb-4">
            <h5 className="mb-3">Documentos predefinidos</h5>
            {isMobile ? (
              <DocsList
                items={nodes}
                onSelect={(nodeData) => {
                  setCurrentReply(nodeData);
                }}
              />
            ) : (
              <Tree
                contents={nodes}
                onNodeClick={onNodeClick}
                onNodeExpand={onNodeExpand}
                onNodeCollapse={onNodeCollapse}
              />
            )}
          </div>
        )}

        {/* Área de carga de archivos - solo si tiene permiso */}
        {canUploadUnreviewed && (
          <>
            {docNodes.length > 0 && <hr className="my-4" />}
            <div className="mb-4">
              <h5 className="mb-3">
                {docNodes.length > 0 ? "O carga un nuevo documento" : "Cargar documento"}
              </h5>
              <div
                style={{
                  border: "2px dashed #ccc",
                  borderRadius: "10px",
                  padding: "20px",
                  backgroundColor: "#f9f9f9",
                  textAlign: "center",
                  maxHeight: "100px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "15px",
                }}
              >
                <i
                  className="fas fa-cloud-upload-alt"
                  style={{
                    fontSize: "2rem",
                    color: "#28a745",
                  }}
                ></i>
                <div style={{ flex: 1 }}>
                  <p style={{ color: "#666", fontSize: "14px", margin: 0 }}>
                    PDF, Word, Excel o Imágenes
                  </p>
                </div>
                <input
                  type="file"
                  id="fileInput"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.bmp,.webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/*"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
                <label
                  htmlFor="fileInput"
                  className="btn btn-primary btn-sm"
                  style={{
                    borderRadius: "20px",
                    cursor: "pointer",
                    margin: 0,
                    whiteSpace: "nowrap",
                  }}
                >
                  <i className="fas fa-folder-open mr-1"></i> Seleccionar
                </label>
              </div>
            </div>
          </>
        )}

        {/* Vista previa unificada al final */}
        {(currentReply.ReplyLink || filePreview) && (
          <>
            <hr className="my-4" />
            <div className="mb-4">
              <h5 className="mb-3 text-success">
                <i className="fas fa-check-circle"></i> Vista previa
              </h5>

              {/* Vista previa de documento predefinido */}
              {currentReply.ReplyLink && !filePreview && (
                <div className="text-center">
                  <embed
                    src={currentReply.ReplyLink}
                    type="application/pdf"
                    style={{
                      width: "100%",
                      maxWidth: "800px",
                      height: isMobile ? "300px" : "500px",
                      border: "2px solid #28a745",
                      borderRadius: "8px",
                    }}
                  />
                  <div className="mt-3">
                    <strong>{currentReply.FileName?.String}</strong>
                  </div>
                </div>
              )}

              {/* Vista previa de archivo PDF cargado */}
              {filePreview && filePreview.type === "pdf" && (
                <div className="text-center">
                  <embed
                    src={filePreview.url}
                    type="application/pdf"
                    style={{
                      width: "100%",
                      maxWidth: "800px",
                      height: isMobile ? "300px" : "500px",
                      border: "2px solid #28a745",
                      borderRadius: "8px",
                    }}
                  />
                  <div className="mt-3">
                    <strong>{filePreview.name}</strong>
                    <br />
                    <small className="text-muted">({filePreview.size})</small>
                  </div>
                  <label
                    htmlFor="fileInput"
                    className="btn btn-outline-secondary btn-sm mt-3"
                    style={{
                      borderRadius: "15px",
                      cursor: "pointer",
                    }}
                  >
                    <i className="fas fa-sync-alt"></i> Cambiar archivo
                  </label>
                </div>
              )}

              {/* Vista previa de imagen cargada */}
              {filePreview && filePreview.type === "image" && (
                <div className="text-center">
                  <img
                    src={filePreview.url}
                    alt={filePreview.name}
                    style={{
                      width: "100%",
                      maxWidth: "800px",
                      maxHeight: isMobile ? "300px" : "500px",
                      objectFit: "contain",
                      border: "2px solid #28a745",
                      borderRadius: "8px",
                    }}
                  />
                  <div className="mt-3">
                    <strong>{filePreview.name}</strong>
                    <br />
                    <small className="text-muted">({filePreview.size})</small>
                  </div>
                  <label
                    htmlFor="fileInput"
                    className="btn btn-outline-secondary btn-sm mt-3"
                    style={{
                      borderRadius: "15px",
                      cursor: "pointer",
                    }}
                  >
                    <i className="fas fa-sync-alt"></i> Cambiar archivo
                  </label>
                </div>
              )}

              {/* Vista previa de Word o Excel */}
              {filePreview && (filePreview.type === "word" || filePreview.type === "excel") && (
                <div className="text-center" style={{ padding: "40px 20px" }}>
                  <div style={{ fontSize: "5rem", marginBottom: "20px" }}>
                    {filePreview.icon}
                  </div>
                  <div style={{ fontSize: "18px", fontWeight: "bold", marginBottom: "10px" }}>
                    {filePreview.name}
                  </div>
                  <div style={{ fontSize: "16px", color: "#666", marginBottom: "10px" }}>
                    {filePreview.size}
                  </div>
                  <div style={{ fontSize: "14px", color: "#999", marginBottom: "20px" }}>
                    {filePreview.type === "word" ? "Documento de Word" : "Hoja de cálculo de Excel"}
                  </div>
                  <label
                    htmlFor="fileInput"
                    className="btn btn-outline-secondary btn-sm"
                    style={{
                      borderRadius: "15px",
                      cursor: "pointer",
                    }}
                  >
                    <i className="fas fa-sync-alt"></i> Cambiar archivo
                  </label>
                </div>
              )}

              {/* Vista previa de texto */}
              {filePreview && filePreview.type === "text" && (
                <div>
                  <div className="text-center mb-3">
                    <div style={{ fontSize: "3rem", marginBottom: "10px" }}>
                      {filePreview.icon}
                    </div>
                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>
                      {filePreview.name}
                    </div>
                    <small className="text-muted">({filePreview.size})</small>
                  </div>
                  <div
                    style={{
                      backgroundColor: "#f5f5f5",
                      padding: "20px",
                      borderRadius: "8px",
                      maxHeight: "400px",
                      overflowY: "auto",
                      textAlign: "left",
                      fontFamily: "monospace",
                      fontSize: "13px",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      border: "2px solid #28a745",
                    }}
                  >
                    {filePreview.content}
                  </div>
                  <div className="text-center mt-3">
                    <label
                      htmlFor="fileInput"
                      className="btn btn-outline-secondary btn-sm"
                      style={{
                        borderRadius: "15px",
                        cursor: "pointer",
                      }}
                    >
                      <i className="fas fa-sync-alt"></i> Cambiar archivo
                    </label>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Footer con botón de enviar */}
      <div
        className="row d-flex flex-column align-items-center p-2 m-0 justify-content-center"
        style={{
          background: "rgba(219,219,219, 0.5)",
          minHeight: "85px",
          position: isMobile ? "fixed" : "relative",
          bottom: isMobile ? 0 : "auto",
          width: "100%",
        }}
      >
        {uploadErrors && (
          <div className="text-light mt-2 mb-1 bg-danger p-2 rounded">
            {uploadErrors}
          </div>
        )}
        <button
          onClick={() => {
            if (selectedFile) {
              handleSendFile();
            } else if (currentReply.ReplyLink) {
              props.onDocumentClick({
                link: currentReply.ReplyLink,
                filename: currentReply.FileName?.String || "Documento",
              }, false);
            }
          }}
          disabled={!selectedFile && !currentReply.ReplyLink}
          type="button"
          style={{ borderRadius: "25px" }}
          className="btn btn-success btn-icon"
        >
          {loading ? (
            <>
              <span
                className="spinner-border spinner-border-sm mr-2"
                role="status"
                aria-hidden="true"
              ></span>
              Enviando...
            </>
          ) : (
            <>
              <i className="fas fa-paper-plane mr-2"></i> Enviar
            </>
          )}
        </button>
      </div>
    </div>
  );
}
